import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Clock,
  Settings,
  Check,
  CircleCheck,
  CircleX,
  Search,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { GetSafetyTask } from "../../services/safetytask";
import { formatDateDMY } from "../../utils";
import { Task } from "../../types/safetytask";
import { DropdownOption } from "../../types/dropdown";
import { fetchDropdownData } from "../../utils";
import SafetyTaskHeader from "../../components/safetyTaskHeader/Index";
import Dropdown from "../../components/dropdown/Index";
import useMallTenantDropdown from "../../hooks/useMallTenantDropdown";

const ITEMS_PER_PAGE = 6;

const SafetyTask = () => {
  const [statusFilter, setStatusFilter] = useState<"All" | Task["SafetyStatusName"]>("All");
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);
  const [safetyTaskData, setSafetyTaskData] = useState<Task[]>([]);
  const [q, setQ] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const fetchSafetyTasks = useCallback(
    async (
      safetytaskid: number,
      tenantid: number,
      safetystatusid: number,
      isoverdue: boolean
    ) => {
      try {
        const response = await GetSafetyTask(
          safetytaskid,
          tenantid,
          safetystatusid,
          isoverdue
        );
        if (response.Code === 0 && Array.isArray(response.safetyTaskDatas)) {
          const mapped = response.safetyTaskDatas.map((t: any) => {
            const statusName = t.SafetyStatusName || (
              t.SafetyStatusId === 1 ? "New" :
              t.SafetyStatusId === 2 ? "In Progress" :
              t.SafetyStatusId === 3 ? "Done" :
              t.SafetyStatusId === 4 ? "Close" :
              t.SafetyStatusId === 5 ? "Reject" : "New"
            );
            return {
              ...t,
              SafetyStatusName: statusName,
            };
          });
          setSafetyTaskData(mapped);
        }
      } catch {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch safety tasks",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    },
    []
  );

  useEffect(() => {
    const storedTenantId =
      localStorage.getItem("tenant_id") || sessionStorage.getItem("tenant_id");
    if (storedTenantId && Number(storedTenantId) !== 0) {
      fetchSafetyTasks(0, Number(storedTenantId), 0, false);
    }
  }, [fetchSafetyTasks]);

  const {
    mallOptions,
    tenantOptions,
    selectedMall,
    selectedTenant,
    setSelectedMall,
    setSelectedTenant,
  } = useMallTenantDropdown((tenantId) => {
    fetchSafetyTasks(0, tenantId, 0, false);
  });

  useEffect(() => {
    fetchDropdownData("safetyStatusOptions")
      .then(setStatusOptions)
      .catch(() =>
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: "Failed to fetch safety options",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        })
      );
  }, []);

  const handleViewTask = (id: number) => {
    navigate(`/SafetyTask/form/${id}`);
  };

  const allStatusFilters = useMemo(() => {
    const defaultStatuses = ["New", "In Progress", "Done", "Close", "Reject"];
    const list = [...defaultStatuses];
    statusOptions.forEach((opt) => {
      if (!list.some((item) => item.toLowerCase() === opt.label.toLowerCase())) {
        list.push(opt.label);
      }
    });
    return list;
  }, [statusOptions]);

  const stats = useMemo(() => {
    return {
      new: safetyTaskData.filter((t) => t.SafetyStatusName === "New").length,
      inProgress: safetyTaskData.filter((t) => t.SafetyStatusName === "In Progress").length,
      done: safetyTaskData.filter((t) => t.SafetyStatusName === "Done").length,
      close: safetyTaskData.filter((t) => t.SafetyStatusName === "Close").length,
      reject: safetyTaskData.filter((t) => t.SafetyStatusName === "Reject").length,
    };
  }, [safetyTaskData]);

  const filtered = useMemo(() => {
    const base =
      statusFilter === "All"
        ? safetyTaskData
        : safetyTaskData.filter((t) => t.SafetyStatusName?.toLowerCase() === statusFilter.toLowerCase());
    const qq = q.trim().toLowerCase();
    if (!qq) return base;
    return base.filter(
      (t) =>
        t.SafetyCategoryName.toLowerCase().includes(qq) ||
        t.Description.toLowerCase().includes(qq) ||
        t.BrandName.toLowerCase().includes(qq) ||
        (t.DueDate || "").toLowerCase().includes(qq)
    );
  }, [q, statusFilter, safetyTaskData]);

  const rawTotalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const totalPages = Math.max(1, rawTotalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [q, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedTasks = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderPageButtons = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);

    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");

    pages.push(totalPages);

    return pages;
  };

  return (
    <>
      <Dropdown
        mallSelected={setSelectedMall}
        mallOptions={mallOptions}
        tenantSelected={setSelectedTenant}
        tenantOptions={tenantOptions}
        mallValue={selectedMall}
        tenantValue={selectedTenant}
      />
      <div className="min-h-screen bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex overflow-x-auto gap-4 pb-3 sm:pb-0 sm:grid sm:grid-cols-5 sm:gap-6 md:gap-8 flex-1 min-w-0 snap-x scrollbar-none">
              <div
                className="min-w-[130px] sm:min-w-0 snap-start flex-shrink-0 cursor-pointer"
                onClick={() => setStatusFilter("New")}
              >
                <SafetyTaskHeader
                  icon={Clock}
                  iconWrapper="bg-gray-100 dark:bg-gray-700"
                  iconStyle="text-gray-600 dark:text-gray-400"
                  title="New"
                  status={stats.new}
                />
              </div>
              <div
                className="min-w-[130px] sm:min-w-0 snap-start flex-shrink-0 cursor-pointer"
                onClick={() => setStatusFilter("In Progress")}
              >
                <SafetyTaskHeader
                  icon={Settings}
                  iconWrapper="bg-blue-100 dark:bg-blue-900/30"
                  iconStyle="text-blue-600 dark:text-blue-400"
                  title="In Progress"
                  status={stats.inProgress}
                />
              </div>
              <div
                className="min-w-[130px] sm:min-w-0 snap-start flex-shrink-0 cursor-pointer"
                onClick={() => setStatusFilter("Done")}
              >
                <SafetyTaskHeader
                  icon={Check}
                  iconWrapper="bg-green-100 dark:bg-green-900/30"
                  iconStyle="text-green-600 dark:text-green-400"
                  title="Done"
                  status={stats.done}
                />
              </div>
              <div
                className="min-w-[130px] sm:min-w-0 snap-start flex-shrink-0 cursor-pointer"
                onClick={() => setStatusFilter("Close")}
              >
                <SafetyTaskHeader
                  icon={CircleCheck}
                  iconWrapper="bg-blue-100 dark:bg-blue-900/30"
                  iconStyle="text-blue-600 dark:text-blue-400"
                  title="Close"
                  status={stats.close}
                />
              </div>
              <div
                className="min-w-[130px] sm:min-w-0 snap-start flex-shrink-0 cursor-pointer"
                onClick={() => setStatusFilter("Reject")}
              >
                <SafetyTaskHeader
                  icon={CircleX}
                  iconWrapper="bg-red-100 dark:bg-red-900/30"
                  iconStyle="text-red-600 dark:text-red-400"
                  title="Reject"
                  status={stats.reject}
                />
              </div>
            </div>
            <div className="hidden lg:flex lg:items-center lg:gap-2 flex-shrink-0">
              <img
                src="/images/icon (4).png"
                alt="Fire Safety"
                className="w-24 h-24"
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-4 sm:mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              aria-label="Search tasks"
            />
          </div>

          <div className="w-full md:w-auto md:ml-2">
            <div className="block md:hidden">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
              >
                <option value="All">All</option>
                {allStatusFilters.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="hidden md:flex md:flex-wrap md:gap-2">
              <button
                onClick={() => setStatusFilter("All")}
                className={`px-6 py-2.5 rounded-xl font-medium transition text-sm ${statusFilter === "All"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-600"
                  }`}
              >
                All
              </button>
              {allStatusFilters.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setStatusFilter(opt as any)}
                  className={`px-6 py-2.5 rounded-xl font-medium transition text-sm ${statusFilter === opt
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-white dark:hover:bg-gray-600"
                    }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {paginatedTasks.map((t) => (
            <div
              key={t.SafetyTaskId}
              className="relative bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col"
              style={{ minHeight: 280 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  <div>
                    Due :{" "}
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDateDMY(t.DueDate)}
                    </span>
                  </div>
                </div>
                <div
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium flex-shrink-0 ml-2 ${t.SafetyStatusName === "New"
                    ? "bg-gray-900 dark:bg-gray-700 text-white"
                    : t.SafetyStatusName === "In Progress"
                      ? "bg-yellow-400 text-gray-900"
                      : t.SafetyStatusName === "Done"
                        ? "bg-green-500 text-white"
                        : t.SafetyStatusName === "Close"
                          ? "bg-blue-500 text-white"
                          : "bg-red-500 text-white"
                    }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.SafetyStatusName === "In Progress"
                      ? "bg-gray-900"
                      : "bg-white"
                      }`}
                  />
                  <span className="whitespace-nowrap">{t.SafetyStatusName}</span>
                </div>
              </div>

              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
                {t.SafetyCategoryName}
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 flex-grow line-clamp-3">
                {t.Description}
              </p>

              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-4">
                <div>
                  <span>Created by : </span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {t.CreatedBy}
                  </span>
                </div>
              </div>

              <div className="mt-auto">
                {t.SafetyStatusName === "Close" ||
                  t.SafetyStatusName === "Reject" ||
                  t.SafetyStatusName === "Done" ? (
                  <button
                    className="w-full py-2.5 sm:py-3 rounded-xl border border-blue-600 text-blue-600 font-medium bg-white dark:bg-gray-800 hover:bg-blue-600 hover:text-white transition text-sm"
                    onClick={() => handleViewTask(t.SafetyTaskId)}
                  >
                    View
                  </button>
                ) : (
                  <button
                    className="w-full py-2.5 sm:py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-white dark:hover:bg-gray-800 hover:text-blue-600 hover:border hover:border-blue-600 transition shadow-sm text-sm"
                    onClick={() => handleViewTask(t.SafetyTaskId)}
                  >
                    Update
                  </button>
                )}
              </div>
            </div>
          ))}

          {paginatedTasks.length === 0 && (
            <div className="col-span-full text-center text-gray-500 dark:text-gray-400 p-8">
              Tidak ada tugas yang cocok dengan filter / pencarian.
            </div>
          )}
        </div>

        <div className="flex items-center justify-center flex-wrap gap-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {renderPageButtons().map((p, idx) =>
            typeof p === "number" ? (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                aria-current={currentPage === p ? "page" : undefined}
                className={`min-w-[36px] sm:min-w-[40px] h-9 sm:h-10 px-2 sm:px-3 rounded-lg font-medium transition text-sm ${currentPage === p
                  ? "bg-blue-600 text-white"
                  : "border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                  }`}
              >
                {p}
              </button>
            ) : (
              <span
                key={`dot-${idx}`}
                className="text-gray-400 dark:text-gray-500 px-1 select-none"
              >
                ...
              </span>
            )
          )}

          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );
};

export default SafetyTask;